import ChatScreen from "./Chat/ChatScreen";
import ContactScreen from "./Contact/ContactScreen";
import MineScreen from "./Mine/MineScreen";
import React, { useEffect, useRef, useState } from "react";
import {
  color_main,
  color_666666,
  transparent,
  white,
} from "../assets/style/colors";
import {
  ActivityIndicator,
  DeviceEventEmitter,
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  Platform,
} from "react-native";
import { useStore } from "../store/UserStore";
import cache from "../cache";
import {
  Str_Chat,
  Str_Contact,
  Str_Mine,
  Str_MsgLoading,
} from "../language/string_res";
import { useTranslation } from "react-i18next";
import globalEventBus from "../utils/EventBus";
import * as ImSdk from "../../imsdk/api";
import { LoginGuideModal } from "./login/LoginGuideModal";
import { useIsFocused } from "@react-navigation/native";
import { useAppNavigation } from "./navigation";
import { TopicWithSummary } from "../../imsdk/api/topic";
import { useChatStore } from "../store/ChatStore";

import ContactSelectIcon from "../assets/svg/icon_tab_contact_select.svg";
import ContactIcon from "../assets/svg/icon_tab_contact_normal.svg";

import ChatSelectIcon from "../assets/svg/icon_tab_chat_select.svg";
import ChatIcon from "../assets/svg/icon_tab_chat_normal.svg";

import MineSelectIcon from "../assets/svg/icon_tab_mine_select.svg";
import MineIcon from "../assets/svg/icon_tab_mine_normal.svg";

import LogoIcon from "../assets/svg/ic_round_logo.svg";
import MisoLogo from "../assets/svg/ic_round_logo_miso.svg";

import { LeftView } from "./Chat/CListItem";
import { getAvatarByUser } from "../utils/utils";

import { FullProcessData, MQEventCallBack } from "../../imsdk/api/mq";
import {
  registerScreens,
  getScreenConfig,
  WebDetailPayload,
} from "../utils/WebNavigationHelper";
import { webNavigableScreens } from "../config/webNavigableScreens";
import { isMiso } from "../../imsdk/api";
import { applyLocalReadToTopicSummaryInList } from "./Chat/chat-helper";

type TabType = "Contact" | "Chat" | "Mine";

// 详情类型 - 简化为通用类型
type DetailType = {
  screenName: string;
  params?: any;
} | null;

export const MainTabScreen: React.FC = () => {
  const im = ImSdk.getInstance();
  const [unreadCnt, setUnreadCnt] = useState(0);
  const [friendRequestCnt, setFriendRequestCnt] = useState(0);
  const { state, dispatch } = useStore();
  const { chatDispatch } = useChatStore();
  const [loginGuideVisible, setLoginGuideVisible] = useState(false);
  const hasShownGuide = useRef<Boolean>(false);
  const [isMsgFullProcess, setIsMsgFullProcess] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TabType>("Contact");
  const [selectedDetail, setSelectedDetail] = useState<DetailType>(null);
  const [selectedTid, setSelectedTid] = useState<string | null>(null); // 当前选中的会话 tid
  const isSwitchingToChat = useRef<boolean>(false); // 标记是否正在切换到 Chat 并显示详情
  const isFocus = useIsFocused();
  const uid = ImSdk.getInstance().CurrentUid();
  const navigation = useAppNavigation();
  // Removed zeroTimer - ChatScreen now handles all emit, no debounce needed
  useTranslation();

  // 注册所有可在 Web 三栏显示的页面
  useEffect(() => {
    registerScreens(webNavigableScreens);
  }, []);

  useEffect(() => {
    if (isFocus && state?.loginGuide && !hasShownGuide.current) {
      setLoginGuideVisible(true);
      hasShownGuide.current = true;
    }
  }, [isFocus, state?.loginGuide]);

  useEffect(() => {
    setFriendRequestCnt(state?.friendApply ?? 0);
  }, [state?.friendApply]);

  useEffect(() => {
    const unreadTotal = cache.Get<number>("unreadTotal");
    if (unreadTotal) {
      setUnreadCnt(unreadTotal);
    }
    DeviceEventEmitter.addListener("unreadTotal", setUnreadCnt);
    im.on("full_process", syncCallback);
    globalEventBus.on("on_refresh", checkNeesShowMsgLoading);
    return () => {
      DeviceEventEmitter.removeAllListeners("unreadTotal");
      im.off("full_process", syncCallback);
      globalEventBus.off("on_refresh");
    };
  }, []);

  // 监听全局事件，用于从子组件触发详情显示
  useEffect(() => {
    // 统一的 Web 详情显示处理
    const handleShowWebDetail = (data: WebDetailPayload) => {
      const config = getScreenConfig(data.screenName);
      if (!config) {
        console.warn(`[MainTabScreen] 未找到页面配置: ${data.screenName}`);
        return;
      }

      // 如果需要切换 tab，标记正在切换
      if (config.tabType && config.tabType !== activeTab) {
        // 如果是切换到 Chat tab，标记防止被清空
        if (config.tabType === "Chat") {
          isSwitchingToChat.current = true;
        }
        // 自动切换 tab
        setActiveTab(config.tabType);
      }

      // 设置详情
      setSelectedDetail({
        screenName: data.screenName,
        params: data.params,
      });

      // 如果是聊天页面，记录选中的 tid
      if (data.screenName === "ChatList" && data.params?.topic?.topic?.tid) {
        setSelectedTid(data.params.topic.topic.tid);
      }

      // 清除标记（延迟执行，确保 useEffect 已经处理完）
      if (config.tabType === "Chat") {
        setTimeout(() => {
          isSwitchingToChat.current = false;
        }, 100);
      }
    };

    // 清空详情
    const handleClearWebDetail = () => {
      setSelectedDetail(null);
      setSelectedTid(null);
    };

    // 处理从联系人详情发送消息：切换到Chat tab，滚动到对应会话，显示聊天
    const handleSwitchToChatAndShow = (data: {
      topic: TopicWithSummary;
      scrollToTid: string;
    }) => {
      // 标记正在切换到 Chat，防止被清空
      isSwitchingToChat.current = true;

      const cleared = applyLocalReadToTopicSummaryInList(data.topic);
      dispatch({ type: "UPSERT_TOPIC_SUMMARY", payload: [cleared] });

      // 1. 先设置详情，再切换 tab（避免被清空）
      setSelectedDetail({
        screenName: "ChatList",
        params: {
          topic: cleared,
        },
      });
      setSelectedTid(cleared.topic.tid); // 记录选中的 tid

      // 2. 更新 chatState.curTopicWithSummary，确保 ChatList 能正确获取 topic
      chatDispatch({ type: "CHAT_SWITCH_TOPIC", payload: cleared });

      // 3. 切换到Chat tab，让ChatScreen开始渲染
      setActiveTab("Chat");

      // 4. 清除标记（延迟执行，确保 useEffect 已经处理完）
      setTimeout(() => {
        isSwitchingToChat.current = false;
      }, 100);

      // 5. 异步刷新会话列表，确保新创建的会话能显示在列表中（不阻塞UI）
      im.GetAllTopicListRemote()
        .then(() => {
          // 刷新完成
        })
        .catch((error) => {
          console.error("❌ [MainTabScreen] 刷新会话列表失败:", error);
        });

      // 6. 通知ChatScreen滚动到对应的会话（延迟执行，确保ChatScreen已渲染）
      setTimeout(() => {
        globalEventBus.emit("ScrollToTopic", { tid: data.scrollToTid });
      }, 500);
    };

    // 统一的事件监听
    globalEventBus.on("ShowWebDetail", handleShowWebDetail);
    globalEventBus.on("ClearWebDetail", handleClearWebDetail);
    globalEventBus.on("SwitchToChatAndShow", handleSwitchToChatAndShow);

    return () => {
      globalEventBus.off("ShowWebDetail");
      globalEventBus.off("ClearWebDetail");
      globalEventBus.off("SwitchToChatAndShow");
    };
  }, [activeTab]);

  // 切换 tab 时清空详情和选中状态（但如果正在切换到 Chat 并显示详情，则不清空）
  useEffect(() => {
    if (isSwitchingToChat.current) {
      // 正在处理 SwitchToChatAndShow，不清空
      return;
    }
    setSelectedDetail(null);
    if (activeTab !== "Chat") {
      setSelectedTid(null);
    }
  }, [activeTab]);

  const syncCallback: MQEventCallBack = async (res) => {
    if (res.type === "full_process" && res.data && res.data.full_process) {
      const full_process = (res.data.full_process as FullProcessData) ?? {};
      checkNeesShowMsgLoading(full_process);
      return;
    }
  };

  const checkNeesShowMsgLoading = async (data: FullProcessData) => {
    if (data.name && data.name.length) {
      setIsMsgFullProcess(data.name !== "done");
    }
  };

  const renderList = () => {
    // 所有屏幕都必须挂载，这样每个屏幕的 useEffect 才能在后台运行
    // React 中即使 display:none，组件仍然会执行 effect，这对我们很重要
    return (
      <>
        {/* ChatScreen 必须始终挂载，以便即使切换 tab 也能监听消息 */}
        <View
          style={{
            position: activeTab === "Chat" ? "relative" : "absolute",
            left: activeTab === "Chat" ? 0 : -9999,
            top: activeTab === "Chat" ? 0 : -9999,
            right: 0,
            bottom: 0,
            width: activeTab === "Chat" ? "100%" : 0,
            height: activeTab === "Chat" ? "100%" : 0,
            overflow: "hidden",
          }}
        >
          <ChatScreen key="chat" selectedTid={selectedTid} />
        </View>

        {/* Contact 屏幕 */}
        {activeTab === "Contact" && <ContactScreen />}

        {/* Mine 屏幕 */}
        {activeTab === "Mine" && <MineScreen />}
      </>
    );
  };

  const renderDetail = () => {
    if (!selectedDetail) {
      return (
        <View style={styles.emptyDetail}>
          {isMiso ? <MisoLogo width={80} height={80} /> : <LogoIcon width={120} height={120} />}
        </View>
      );
    }

    // 动态渲染注册的页面组件
    const config = getScreenConfig(selectedDetail.screenName);
    if (!config) {
      console.error(
        `[MainTabScreen] 未找到页面配置: ${selectedDetail.screenName}`
      );
      return (
        <View style={styles.emptyDetail}>
          <Text style={styles.emptyDetailText}>页面未找到</Text>
        </View>
      );
    }

    const Component = config.component;
    // 确保 params 是一个对象，并且清理可能包含文本节点的值
    const safeParams =
      selectedDetail.params && typeof selectedDetail.params === "object"
        ? selectedDetail.params
        : {};

    return (
      <Component
        navigation={navigation as any}
        route={
          {
            params: safeParams,
            key: selectedDetail.screenName,
            name: selectedDetail.screenName,
          } as any
        }
      />
    );
  };

  const renderTabItem = (
    tab: TabType,
    label: string,
    Icon: any,
    SelectedIcon: any,
    badge?: number | string
  ) => {
    const isActive = activeTab === tab;
    const IconComponent = isActive ? SelectedIcon : Icon;

    return (
      <TouchableOpacity
        key={tab}
        style={[styles.tabItem, isActive && styles.tabItemActive]}
        onPress={() => setActiveTab(tab)}
        activeOpacity={0.7}
      >
        <View style={styles.tabIconContainer}>
          <IconComponent width={24} height={24} />
          {badge !== undefined && badge !== null && (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>
                {typeof badge === "number" && badge > 99 ? "99+" : badge}
              </Text>
            </View>
          )}
        </View>
        <Text style={[styles.tabLabel, isActive && styles.tabLabelActive]}>
          {label}
        </Text>
      </TouchableOpacity>
    );
  };

  const user = state?.user;
  const totalUnread = unreadCnt; // 总未读数用于头像上的标记

  return (
    <View style={styles.container}>
      {/* 第一栏：左侧导航栏 */}
      <View style={styles.sidebar}>
        {/* 顶部：用户头像 */}
        <View style={styles.sidebarHeader}>
          {user && (
            <View style={styles.avatarContainer}>
              <LeftView left={getAvatarByUser(user)} size={50} isRounded={true} />
              {totalUnread > 0 && (
                <View style={styles.avatarBadge}>
                  <Text style={styles.avatarBadgeText}>
                    {totalUnread > 99 ? "99+" : totalUnread}
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>

        {/* 导航标签 */}
        <View style={styles.tabContainer}>
          {renderTabItem(
            "Contact",
            Str_Contact,
            ContactIcon,
            ContactSelectIcon,
            friendRequestCnt > 0 ? friendRequestCnt : undefined
          )}
          {renderTabItem(
            "Chat",
            Str_Chat,
            ChatIcon,
            ChatSelectIcon,
            unreadCnt > 0 ? unreadCnt : undefined
          )}
          {renderTabItem("Mine", Str_Mine, MineIcon, MineSelectIcon)}
        </View>
      </View>

      {/* 第二栏：中间列表区域 */}
      <View style={styles.listContainer}>{renderList()}</View>

      {/* 第三栏：右侧详情区域 */}
      <View style={styles.detailContainer}>{renderDetail()}</View>

      {/* PC端不做这个 */}
      {/* {isMsgFullProcess && (
        <View style={styles.msgLoading}>
          <ActivityIndicator size="small" color={color_main} />
          <Text style={styles.loadingText}>{Str_MsgLoading}</Text>
        </View>
      )} */}
      <LoginGuideModal
        uid={uid}
        visible={loginGuideVisible}
        onClose={() => {
          setLoginGuideVisible(false);
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
        flex: 1,
        flexDirection: "row",
        backgroundColor: "#F5F7FA",
    },
  sidebar: {
    width: 88,
    backgroundColor: white,
    borderRightWidth: 0,
    shadowColor: "#000",
    shadowOffset: {
      width: 2,
      height: 0,
    },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
    alignItems: 'center',
  },
  sidebarHeader: {
    width: "100%",
    alignItems: "center",
    paddingTop: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E5E5",
  },
  avatarContainer: {
    position: "relative",
    marginBottom: 12,
  },
  avatarBadge: {
    position: "absolute",
    top: -4,
    right: -4,
    backgroundColor: "#FF3B30",
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    paddingHorizontal: 6,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: white,
  },
  avatarBadgeText: {
    color: white,
    fontSize: 10,
    fontWeight: "bold",
  },
  sidebarActionButton: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    borderRadius: 8,
    ...(Platform.OS === "web" && {
      cursor: "pointer",
    }),
  },
  tabContainer: {
    flex: 1,
    width: "100%",
    paddingTop: 8,
  },
  listContainer: {
    width: 340,
    backgroundColor: white,
    borderRightWidth: 0,
    shadowColor: "#000",
    shadowOffset: {
      width: 2,
      height: 0,
    },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  detailContainer: {
    flex: 1,
    minWidth: 300,
    backgroundColor: white,
  },
  emptyDetail: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: "#F5F7FA",
  },
  emptyDetailText: {
    fontSize: 14,
    color: color_666666,
  },
  tabItem: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 16,
    paddingHorizontal: 8,
    marginBottom: 4,
    borderRadius: 8,
    ...(Platform.OS === "web" && {
      cursor: "pointer",
      transition: "background-color 0.2s",
    }),
  },
  tabItemActive: {
    backgroundColor: "#E8F5E9", // 浅绿色背景，更接近图片效果
  },
  tabIconContainer: {
    position: "relative",
    marginBottom: 4,
  },
  tabLabel: {
    fontSize: 12,
    color: color_666666,
    marginTop: 4,
    textAlign: "center",
  },
  tabLabelActive: {
    color: color_main,
    fontWeight: "500",
  },
  badge: {
    position: "absolute",
    top: -6,
    right: -8,
    backgroundColor: "#FF3B30",
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    paddingHorizontal: 6,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: white,
  },
  badgeText: {
    color: white,
    fontSize: 10,
    fontWeight: "bold",
  },
  msgLoading: {
    position: "absolute",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9,
    top: 0,
    left: 120,
    right: 120,
    height: 44,
    backgroundColor: transparent,
  },
  loadingText: {
    color: color_main,
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    fontFamily: "",
    flex: 1,
  },
});
